import { useRef } from 'react'
import { X, GripVertical } from 'lucide-react'

interface FloatingPanelProps {
  title: string
  pos: { x: number; y: number }
  onMove: (pos: { x: number; y: number }) => void
  // 未指定なら × を表示しない（閉じられない）
  onClose?: () => void
  // パネル幅の Tailwind クラス（既定は w-56）
  width?: string
  // 高さ(px)。onResize と併用すると下辺ドラッグでリサイズ可能。未指定なら内容に合わせる。
  height?: number
  onResize?: (height: number) => void
  minHeight?: number
  children: React.ReactNode
}

// 地図上に浮かぶドラッグ可能なパネル（Illustrator のプロパティ風）。
// ヘッダをドラッグして移動、× で閉じる。onResize 指定時は下辺で縦リサイズ。
export default function FloatingPanel({
  title,
  pos,
  onMove,
  onClose,
  width = 'w-56',
  height,
  onResize,
  minHeight = 120,
  children,
}: FloatingPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    px: number
    py: number
    x: number
    y: number
  } | null>(null)
  const resizeRef = useRef<{ py: number; h: number } | null>(null)

  const handlePointerDown = (e: React.PointerEvent) => {
    dragRef.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y }
    ;(e.target as Element).setPointerCapture(e.pointerId)
  }
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    onMove({
      x: dragRef.current.x + (e.clientX - dragRef.current.px),
      y: dragRef.current.y + (e.clientY - dragRef.current.py),
    })
  }
  const handlePointerUp = () => {
    dragRef.current = null
  }

  const handleResizeDown = (e: React.PointerEvent) => {
    const h = containerRef.current?.getBoundingClientRect().height ?? minHeight
    resizeRef.current = { py: e.clientY, h }
    ;(e.target as Element).setPointerCapture(e.pointerId)
  }
  const handleResizeMove = (e: React.PointerEvent) => {
    if (!resizeRef.current || !onResize) return
    onResize(
      Math.max(
        minHeight,
        resizeRef.current.h + (e.clientY - resizeRef.current.py)
      )
    )
  }
  const handleResizeUp = () => {
    resizeRef.current = null
  }

  return (
    <div
      ref={containerRef}
      className={`absolute z-30 ${width} bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col overflow-hidden`}
      style={{ left: pos.x, top: pos.y, height }}
    >
      {/* ヘッダ（ドラッグハンドル） */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="flex items-center gap-1 px-2 py-1.5 border-b bg-gray-50 cursor-move select-none"
      >
        <GripVertical size={14} className="text-gray-400" />
        <span className="text-xs font-semibold text-gray-700">{title}</span>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-700"
            aria-label="close"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* 本体 */}
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>

      {/* 下辺リサイズハンドル */}
      {onResize && (
        <div
          onPointerDown={handleResizeDown}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeUp}
          className="h-2.5 flex items-center justify-center border-t bg-gray-50 cursor-ns-resize select-none"
          aria-label="resize"
        >
          <div className="w-8 h-0.5 bg-gray-300 rounded-full" />
        </div>
      )}
    </div>
  )
}
